import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, CheckCircle2, ChevronDown, ChevronUp, Download, ImagePlus, Images, Lock, Move, Palette, PanelsTopLeft, Sparkles, Trash2, Type } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PUBLISHING_FONTS_URL, PUBLISHING_FONT_OPTIONS, PUBLISHING_GRADIENTS } from '../lib/publishingDesignOptions';

const CANVAS_WIDTH = 1024;
const CANVAS_HEIGHT = 500;
const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const drawGraphic = (canvas, options) => {
  const { appName, font, fontSize, textColor, gradient, textPosition, logoImage, logoPosition, logoSize, draggingTarget = null } = options;
  const context = canvas.getContext('2d');
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;

  const fill = context.createLinearGradient(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
  gradient.colors.forEach((color, index) => fill.addColorStop(index / (gradient.colors.length - 1), color));
  context.fillStyle = fill;
  context.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
  const glow = context.createRadialGradient(CANVAS_WIDTH * 0.18, CANVAS_HEIGHT * 0.15, 0, CANVAS_WIDTH * 0.18, CANVAS_HEIGHT * 0.15, CANVAS_WIDTH * 0.65);
  glow.addColorStop(0, 'rgba(255, 255, 255, 0.24)');
  glow.addColorStop(1, 'rgba(255, 255, 255, 0)');
  context.fillStyle = glow;
  context.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  let logoBounds = null;
  if (logoImage) {
    const requestedWidth = CANVAS_WIDTH * (logoSize / 100);
    const ratio = logoImage.naturalHeight / logoImage.naturalWidth;
    const drawWidth = Math.min(requestedWidth, CANVAS_WIDTH * 0.55);
    const drawHeight = Math.min(drawWidth * ratio, CANVAS_HEIGHT * 0.72);
    const adjustedWidth = drawHeight === drawWidth * ratio ? drawWidth : drawHeight / ratio;
    const logoX = clamp(logoPosition.x * CANVAS_WIDTH, adjustedWidth / 2 + 12, CANVAS_WIDTH - adjustedWidth / 2 - 12);
    const logoY = clamp(logoPosition.y * CANVAS_HEIGHT, drawHeight / 2 + 12, CANVAS_HEIGHT - drawHeight / 2 - 12);
    context.save();
    context.shadowColor = 'rgba(0, 0, 0, 0.22)';
    context.shadowBlur = 14;
    context.shadowOffsetY = 4;
    context.drawImage(logoImage, logoX - adjustedWidth / 2, logoY - drawHeight / 2, adjustedWidth, drawHeight);
    context.restore();
    logoBounds = { x: logoX - adjustedWidth / 2, y: logoY - drawHeight / 2, width: adjustedWidth, height: drawHeight };
  }

  const text = appName.trim() || 'Your App Name';
  context.save();
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  let fittedSize = fontSize;
  context.font = `700 ${fittedSize}px "${font}", sans-serif`;
  while (context.measureText(text).width > CANVAS_WIDTH * 0.84 && fittedSize > 36) {
    fittedSize -= 2;
    context.font = `700 ${fittedSize}px "${font}", sans-serif`;
  }
  const textWidth = context.measureText(text).width;
  const textX = clamp(textPosition.x * CANVAS_WIDTH, textWidth / 2 + 18, CANVAS_WIDTH - textWidth / 2 - 18);
  const textY = clamp(textPosition.y * CANVAS_HEIGHT, fittedSize * 0.7 + 16, CANVAS_HEIGHT - fittedSize * 0.7 - 16);
  context.shadowColor = 'rgba(0, 0, 0, 0.28)';
  context.shadowBlur = 18;
  context.shadowOffsetY = 6;
  context.fillStyle = textColor;
  context.fillText(text, textX, textY);
  context.restore();
  const textBounds = { x: textX - textWidth / 2 - 16, y: textY - fittedSize * 0.7, width: textWidth + 32, height: fittedSize * 1.4 };

  if (draggingTarget) {
    const activePosition = draggingTarget === 'logo' ? logoPosition : textPosition;
    const centeredHorizontally = Math.abs(activePosition.x - 0.5) <= 0.012;
    const centeredVertically = Math.abs(activePosition.y - 0.5) <= 0.025;
    context.save();
    context.setLineDash([10, 8]);
    context.lineWidth = 2;
    context.shadowColor = 'rgba(0, 0, 0, 0.55)';
    context.shadowBlur = 4;

    context.strokeStyle = centeredHorizontally ? '#facc15' : 'rgba(255, 255, 255, 0.8)';
    context.beginPath();
    context.moveTo(CANVAS_WIDTH / 2, 0);
    context.lineTo(CANVAS_WIDTH / 2, CANVAS_HEIGHT);
    context.stroke();

    context.strokeStyle = centeredVertically ? '#facc15' : 'rgba(255, 255, 255, 0.8)';
    context.beginPath();
    context.moveTo(0, CANVAS_HEIGHT / 2);
    context.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT / 2);
    context.stroke();

    context.setLineDash([]);
    context.shadowBlur = 0;
    context.fillStyle = centeredHorizontally || centeredVertically ? '#facc15' : 'rgba(255, 255, 255, 0.92)';
    context.beginPath();
    context.arc(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, 6, 0, Math.PI * 2);
    context.fill();
    context.font = '700 15px Arial, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'top';
    context.fillText('CENTER', CANVAS_WIDTH / 2, 12);
    context.restore();
  }

  context.save();
  context.font = '600 18px Arial, sans-serif';
  context.textAlign = 'right';
  context.textBaseline = 'bottom';
  context.fillStyle = 'rgba(255, 255, 255, 0.88)';
  context.shadowColor = 'rgba(0, 0, 0, 0.35)';
  context.shadowBlur = 4;
  context.fillText('allsmalltools.com', CANVAS_WIDTH - 22, CANVAS_HEIGHT - 18);
  context.restore();
  return { text: textBounds, logo: logoBounds };
};

const FeatureGraphicGeneratorPage = () => {
  const canvasRef = useRef(null);
  const logoInputRef = useRef(null);
  const hitAreasRef = useRef({ text: null, logo: null });
  const dragTargetRef = useRef(null);
  const logoUrlRef = useRef('');
  const [appName, setAppName] = useState('My Awesome App');
  const [font, setFont] = useState('Poppins');
  const [fontSize, setFontSize] = useState(84);
  const [textColor, setTextColor] = useState('#ffffff');
  const [gradientId, setGradientId] = useState(PUBLISHING_GRADIENTS[0].id);
  const [textPosition, setTextPosition] = useState({ x: 0.5, y: 0.5 });
  const [logoPosition, setLogoPosition] = useState({ x: 0.22, y: 0.5 });
  const [logoSize, setLogoSize] = useState(22);
  const [logoImage, setLogoImage] = useState(null);
  const [logoName, setLogoName] = useState('');
  const [fontLibraryReady, setFontLibraryReady] = useState(false);
  const [draggingTarget, setDraggingTarget] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const gradient = PUBLISHING_GRADIENTS.find(({ id }) => id === gradientId) || PUBLISHING_GRADIENTS[0];

  useEffect(() => {
    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = PUBLISHING_FONTS_URL;
    stylesheet.dataset.featureGraphicFonts = 'true';
    stylesheet.onload = () => setFontLibraryReady(true);
    stylesheet.onerror = () => setFontLibraryReady(true);
    document.head.appendChild(stylesheet);
    return () => stylesheet.remove();
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !fontLibraryReady) return;
    let cancelled = false;
    const render = async () => {
      await document.fonts.load(`700 84px "${font}"`);
      if (!cancelled && canvasRef.current) hitAreasRef.current = drawGraphic(canvasRef.current, { appName, font, fontSize, textColor, gradient, textPosition, logoImage, logoPosition, logoSize, draggingTarget });
    };
    render();
    return () => { cancelled = true; };
  }, [appName, font, fontSize, textColor, gradient, textPosition, logoImage, logoPosition, logoSize, draggingTarget, fontLibraryReady]);

  useEffect(() => () => { if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current); }, []);

  const uploadLogo = (file) => {
    if (!file || !['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return;
    if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
    const url = URL.createObjectURL(file);
    logoUrlRef.current = url;
    const image = new Image();
    image.onload = () => {
      setLogoImage(image);
      setLogoName(file.name);
      setLogoPosition({ x: 0.22, y: 0.5 });
      setTextPosition({ x: 0.64, y: 0.5 });
    };
    image.src = url;
  };

  const removeLogo = () => {
    if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
    logoUrlRef.current = '';
    setLogoImage(null);
    setLogoName('');
    setTextPosition({ x: 0.5, y: 0.5 });
  };

  const canvasPoint = (event) => {
    const rectangle = canvasRef.current.getBoundingClientRect();
    return { x: (event.clientX - rectangle.left) * (CANVAS_WIDTH / rectangle.width), y: (event.clientY - rectangle.top) * (CANVAS_HEIGHT / rectangle.height) };
  };
  const isInside = (point, bounds) => bounds && point.x >= bounds.x && point.x <= bounds.x + bounds.width && point.y >= bounds.y && point.y <= bounds.y + bounds.height;
  const handlePointerDown = (event) => {
    const point = canvasPoint(event);
    if (isInside(point, hitAreasRef.current.logo)) dragTargetRef.current = 'logo';
    else if (isInside(point, hitAreasRef.current.text)) dragTargetRef.current = 'text';
    else return;
    setDraggingTarget(dragTargetRef.current);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handlePointerMove = (event) => {
    if (!dragTargetRef.current) return;
    const point = canvasPoint(event);
    const position = { x: clamp(point.x / CANVAS_WIDTH, 0.02, 0.98), y: clamp(point.y / CANVAS_HEIGHT, 0.04, 0.96) };
    if (dragTargetRef.current === 'logo') setLogoPosition(position);
    else setTextPosition(position);
  };
  const stopDragging = (event) => {
    dragTargetRef.current = null;
    setDraggingTarget(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const downloadGraphic = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    await document.fonts.load(`700 84px "${font}"`);
    hitAreasRef.current = drawGraphic(canvas, { appName, font, fontSize, textColor, gradient, textPosition, logoImage, logoPosition, logoSize });
    const blob = await new Promise((resolve, reject) => canvas.toBlob((result) => result ? resolve(result) : reject(new Error('Could not create the feature graphic.')), 'image/png'));
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const fileName = appName.trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'app';
    link.href = url;
    link.download = `${fileName}-feature-graphic-1024x500.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    setShowDownloadModal(true);
  };

  const faqs = [
    { question: 'What size is the downloaded graphic?', answer: 'Every feature graphic is downloaded as a 1024 × 500 pixel PNG, ready for the Google Play feature graphic field.' },
    { question: 'How do I position the text and logo?', answer: 'Drag the app name or uploaded logo directly on the preview canvas. Each element can be moved independently anywhere within the safe canvas boundary.' },
    { question: 'Are my logo and app name uploaded?', answer: 'No. The design is rendered locally in your browser. Your logo, app name, and generated graphic are not sent to AllSmallTools.' },
    { question: 'Can I remove the watermark?', answer: 'Generated graphics include a small allsmalltools.com watermark in the bottom-right corner.' },
  ];

  return (
    <>
      <Helmet>
        <title>Google Play Feature Graphic Generator | AllSmallTooLS</title>
        <meta name="description" content="Create a 1024x500 Google Play feature graphic with movable text, your logo, 45 free fonts, and 10 colorful gradients." />
        <meta name="keywords" content="feature graphic generator, Google Play feature graphic, 1024x500 graphic, app store graphic" />
        <link rel="canonical" href="https://allsmalltools.com/feature-graphic-generator" />
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/"><Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Back to Home</Button></Link>
          <div><h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2"><PanelsTopLeft className="h-8 w-8 text-fuchsia-600" /><span>Feature Graphic Generator</span></h1><p className="text-gray-600">Create a customizable 1024 × 500 feature graphic for Google Play</p></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader><CardTitle>Graphic Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2"><Label htmlFor="app-name">App Name</Label><Input id="app-name" value={appName} onChange={(event) => setAppName(event.target.value.slice(0, 60))} placeholder="Enter your app name" className="h-12 text-lg" /><p className="text-xs text-gray-500 text-right">{appName.length}/60 characters</p></div>

              <div className="space-y-2">
                <Label>Font</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="outline" className="h-11 w-full justify-between font-normal"><span className="text-base" style={{ fontFamily: `"${font}", sans-serif` }}>{font}</span><ChevronDown className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="max-h-80 w-72 overflow-y-auto">{PUBLISHING_FONT_OPTIONS.map((option) => <DropdownMenuItem key={option} onSelect={() => setFont(option)} className="flex cursor-pointer justify-between py-2.5"><span className="text-lg" style={{ fontFamily: `"${option}", sans-serif` }}>{option}</span>{font === option && <Check className="h-4 w-4 text-fuchsia-600" />}</DropdownMenuItem>)}</DropdownMenuContent>
                </DropdownMenu>
                <p className="text-xs text-gray-500">The same 45 free fonts available in Screenshot Generator</p>
              </div>

              <div className="space-y-4"><div className="flex items-center justify-between"><Label>Font Size</Label><span className="rounded bg-gray-100 px-2 py-1 text-sm font-medium">{fontSize}px</span></div><Slider value={[fontSize]} min={40} max={120} step={2} onValueChange={([value]) => setFontSize(value)} aria-label="App name font size" /></div>

              <div className="space-y-2">
                <Label htmlFor="feature-text-color">Text Color</Label>
                <div className="flex gap-2"><Input id="feature-text-color" type="color" value={textColor} onChange={(event) => setTextColor(event.target.value)} className="w-14 p-1" /><Input value={textColor} onChange={(event) => setTextColor(event.target.value)} aria-label="Feature graphic text color value" /></div>
              </div>

              <div className="space-y-2">
                <Label>Background Gradient</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="outline" className="h-11 w-full justify-between gap-3 font-normal"><span className="h-6 w-14 rounded" style={{ background: `linear-gradient(135deg, ${gradient.colors.join(', ')})` }} /><span className="flex-1 text-left">{gradient.name}</span><ChevronDown className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-72">{PUBLISHING_GRADIENTS.map((option) => <DropdownMenuItem key={option.id} onSelect={() => setGradientId(option.id)} className="flex cursor-pointer gap-3 py-2.5"><span className="h-8 w-16 rounded" style={{ background: `linear-gradient(135deg, ${option.colors.join(', ')})` }} /><span className="flex-1">{option.name}</span>{gradientId === option.id && <Check className="h-4 w-4 text-fuchsia-600" />}</DropdownMenuItem>)}</DropdownMenuContent>
                </DropdownMenu>
                <p className="text-xs text-gray-500">The same 10 gradients available in Screenshot Generator</p>
              </div>

              <div className="space-y-3">
                <Label>App Logo <span className="font-normal text-gray-400">(optional)</span></Label>
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(event) => { uploadLogo(event.target.files?.[0]); event.target.value = ''; }} />
                {!logoImage ? <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} className="h-12 w-full border-dashed"><ImagePlus className="mr-2 h-5 w-5" />Upload PNG, JPEG, or WebP Logo</Button> : <div className="flex items-center gap-3 rounded-lg border bg-gray-50 p-3"><img src={logoUrlRef.current} alt="Uploaded app logo" className="h-12 w-12 rounded object-contain" /><span className="min-w-0 flex-1 truncate text-sm">{logoName}</span><Button type="button" variant="ghost" size="sm" onClick={removeLogo} aria-label="Remove uploaded logo"><Trash2 className="h-4 w-4 text-red-600" /></Button></div>}
                {logoImage && <><div className="flex items-center justify-between"><span className="text-sm text-gray-600">Logo Size</span><span className="text-sm font-medium">{logoSize}%</span></div><Slider value={[logoSize]} min={8} max={45} step={1} onValueChange={([value]) => setLogoSize(value)} aria-label="Logo size" /></>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><div className="flex items-center justify-between"><CardTitle>Feature Graphic Preview</CardTitle><span className="flex items-center gap-1 text-xs text-fuchsia-700"><Move className="h-4 w-4" />Drag elements</span></div></CardHeader>
            <CardContent className="space-y-5">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-100 shadow-inner"><canvas ref={canvasRef} width="1024" height="500" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={stopDragging} onPointerCancel={stopDragging} className="block h-auto w-full touch-none cursor-move" aria-label="Interactive Google Play feature graphic preview. Drag the text or logo to reposition it." /></div>
              <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50 p-3 text-sm text-fuchsia-900"><strong>Position elements:</strong> Drag the app name or logo directly on the canvas. Use the logo-size slider to resize the logo.</div>
              <div className="flex flex-wrap gap-2"><Button variant="outline" size="sm" onClick={() => setTextPosition({ x: logoImage ? 0.64 : 0.5, y: 0.5 })}>Reset Text Position</Button>{logoImage && <Button variant="outline" size="sm" onClick={() => setLogoPosition({ x: 0.22, y: 0.5 })}>Reset Logo Position</Button>}</div>
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600"><span>PNG format</span><span className="font-semibold text-gray-900">1024 × 500 pixels</span></div>
              <Button onClick={downloadGraphic} disabled={!fontLibraryReady} className="w-full bg-gradient-to-r from-fuchsia-500 to-indigo-600 hover:from-fuchsia-600 hover:to-indigo-700"><Download className="h-4 w-4 mr-2" />{fontLibraryReady ? 'Download Feature Graphic' : 'Loading Font Library…'}</Button>
              <p className="flex items-center justify-center gap-2 text-xs text-gray-500"><Lock className="h-4 w-4" />Created privately in your browser</p>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-12"><CardHeader><h2 className="text-2xl font-bold text-gray-900">Why Use Our Feature Graphic Generator?</h2></CardHeader><CardContent><div className="grid grid-cols-1 md:grid-cols-3 gap-6">{[[Palette, 'Ready-Made Styles', 'Use the same 45 free fonts and 10 polished gradients available in the Screenshot Generator.'], [Type, 'Flexible Canvas', 'Drag your app name and uploaded logo independently to create the composition you want.'], [Sparkles, 'Instant Play Store Asset', 'Preview and download a crisp 1024 × 500 PNG without design software or an account.']].map(([Icon, title, description]) => <div key={title} className="flex flex-col items-start space-y-3"><div className="bg-fuchsia-100 p-3 rounded-lg"><Icon className="h-6 w-6 text-fuchsia-700" /></div><h3 className="text-lg font-semibold text-gray-900">{title}</h3><p className="text-gray-600">{description}</p></div>)}</div></CardContent></Card>

        <Card><CardHeader><h2 className="text-xl font-bold text-gray-900">How to Create a Feature Graphic</h2></CardHeader><CardContent><ol className="space-y-4 text-gray-700">{[['Enter Your App Name', 'Type the name you want displayed and choose its font and size.'], ['Choose a Gradient', 'Pick one of the 10 visual background gradients.'], ['Add and Position Your Logo', 'Upload an optional logo, then drag both the text and logo anywhere on the preview.'], ['Preview and Download', 'Review the live canvas and download the 1024 × 500 PNG.']].map(([title, description], index) => <li key={title} className="flex items-start space-x-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center text-sm font-semibold">{index + 1}</span><div><strong>{title}:</strong> {description}</div></li>)}</ol></CardContent></Card>

        <Card><CardHeader><h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2></CardHeader><CardContent><div className="grid grid-cols-1 md:grid-cols-2 gap-4">{faqs.map((faq, index) => <div key={faq.question} className="border border-gray-200 rounded-lg overflow-hidden"><button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100" aria-expanded={openFaq === index}><h3 className="font-semibold text-gray-900">{faq.question}</h3>{openFaq === index ? <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />}</button>{openFaq === index && <div className="p-4 bg-white"><p className="text-gray-700">{faq.answer}</p></div>}</div>)}</div></CardContent></Card>
      </div>

      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="items-center text-center">
            <div className="mb-2 rounded-full bg-green-100 p-3"><CheckCircle2 className="h-8 w-8 text-green-600" /></div>
            <DialogTitle className="text-2xl">Thank You!</DialogTitle>
            <DialogDescription className="text-center text-base">Your 1024 × 500 feature graphic is being downloaded as a PNG.</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-indigo-50 p-4 text-center">
            <p className="font-semibold text-gray-900">Need App Store screenshots too?</p>
            <p className="mt-1 text-sm text-gray-600">Create promotional iOS and Android screenshots with device frames, custom text, fonts, and gradients.</p>
          </div>
          <Button asChild className="h-11 w-full bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700">
            <Link to="/app-screenshot-generator" onClick={() => setShowDownloadModal(false)}><Images className="h-4 w-4" />Create App Screenshots</Link>
          </Button>
          <Button variant="ghost" onClick={() => setShowDownloadModal(false)} className="w-full">Maybe Later</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeatureGraphicGeneratorPage;
